<template>
	<v-dialog :value="dialog" persistent max-width="640px" @input="onDialogInput">
		<v-card>
			<v-toolbar flat dense>
				<v-toolbar-title class="subheading">{{ dialogTitle }}</v-toolbar-title>
			</v-toolbar>

			<v-card-text>
				<v-autocomplete
					v-if="!hasFixedForm"
					v-model="selectedFormId"
					:items="formOptions"
					item-text="name"
					item-value="id"
					label="Form"
					placeholder="Select a form"
					:return-object="false"
					persistent-hint
					hint="Choose the form this employee needs to complete."
				/>

				<v-autocomplete
					v-if="!hasFixedEmployee"
					v-model="selectedEmployeeId"
					:items="employeeOptions"
					item-text="fullName"
					item-value="id"
					label="Employee"
					placeholder="Select an employee"
					:return-object="false"
					persistent-hint
					hint="Choose who needs to complete this form."
				/>

				<div class="completion-notification mt-4">
					<v-layout align-center>
						<v-flex shrink>
							<v-icon class="mr-2" color="grey darken-1">mdi-email-outline</v-icon>
						</v-flex>
						<v-flex class="text-xs-left">
							<div class="body-2">Completion notification</div>
							<div class="caption text--secondary">{{ notificationSummary }}</div>
						</v-flex>
						<v-flex shrink>
							<v-btn
								small
								flat
								color="primary"
								:disabled="!selectedEmployeeId"
								@click="notificationDetailsExpanded = !notificationDetailsExpanded"
							>
								{{ notificationDetailsExpanded ? 'Done' : 'Change' }}
							</v-btn>
						</v-flex>
					</v-layout>

					<v-expand-transition>
						<div v-show="notificationDetailsExpanded && selectedEmployeeId" class="completion-notification-options">
							<v-checkbox
								v-if="hasEmployeeManager"
								v-model="notifyManager"
								class="mt-2"
								:label="managerNotificationLabel"
								hide-details
							/>

							<v-autocomplete
								v-model="selectedNotificationEmployeeIds"
								:items="notificationEmployeeOptions"
								item-text="fullName"
								item-value="id"
								label="People to notify"
								placeholder="Search employees"
								multiple
								chips
								deletable-chips
								clearable
								:return-object="false"
								class="mt-2"
							/>
						</div>
					</v-expand-transition>
				</div>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn flat @click="close">Cancel</v-btn>
				<v-btn color="primary" :loading="isAssigning" :disabled="!canAssign" @click="submitAssign">Assign form</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
export default {
	name: 'FormAssignmentDialog',
	props: {
		dialog: {
			type: Boolean,
			default: false
		},
		title: {
			type: String,
			default: ''
		},
		forms: {
			type: Array,
			default: () => []
		},
		employees: {
			type: Array,
			default: () => []
		},
		fixedFormId: {
			type: String,
			default: null
		},
		fixedFormName: {
			type: String,
			default: ''
		},
		fixedEmployeeId: {
			type: String,
			default: null
		},
		fixedEmployeeName: {
			type: String,
			default: ''
		},
		fixedEmployeeManagerId: {
			type: String,
			default: null
		},
		fixedEmployeeManagerName: {
			type: String,
			default: ''
		},
		isAssigning: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			selectedFormId: null,
			selectedEmployeeId: null,
			notifyManager: false,
			selectedNotificationEmployeeIds: [],
			notificationDetailsExpanded: false
		};
	},
	computed: {
		dialogTitle() {
			if (this.title) return this.title;
			if (this.hasFixedForm) return `Assign ${this.fixedFormName || 'form'}`;
			if (this.hasFixedEmployee) return `Assign Form to ${this.fixedEmployeeName || 'employee'}`;
			return 'Assign form';
		},
		hasFixedForm() {
			return !!this.fixedFormId;
		},
		hasFixedEmployee() {
			return !!this.fixedEmployeeId;
		},
		formOptions() {
			return this.forms
				.filter(form => form && form.id && (!form.status || form.status === 'Active'))
				.slice()
				.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
		},
		employeeOptions() {
			return this.employees
				.filter(employee => employee && employee.id)
				.slice()
				.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
		},
		selectedEmployee() {
			return this.employeeOptions.find(employee => employee.id === this.selectedEmployeeId) || null;
		},
		selectedEmployeeManagerId() {
			if (this.hasFixedEmployee) return this.fixedEmployeeManagerId;
			return this.selectedEmployee ? this.selectedEmployee.reportsTo : null;
		},
		selectedEmployeeManagerName() {
			if (this.hasFixedEmployee && this.fixedEmployeeManagerName) return this.fixedEmployeeManagerName;

			const manager = this.employeeOptions.find(employee => employee.id === this.selectedEmployeeManagerId);
			return manager ? manager.fullName : '';
		},
		hasEmployeeManager() {
			return !!this.selectedEmployeeManagerId;
		},
		managerNotificationLabel() {
			return this.selectedEmployeeManagerName ? `Notify manager (${this.selectedEmployeeManagerName})` : 'Notify manager';
		},
		notificationSummary() {
			if (!this.selectedEmployeeId) {
				return 'Choose an employee to configure completion notifications.';
			}

			const people = [];

			if (this.notifyManager && this.hasEmployeeManager) {
				people.push(this.selectedEmployeeManagerName ? `${this.selectedEmployeeManagerName} (manager)` : 'manager');
			}

			if (this.selectedNotificationEmployeeIds.length === 1) {
				const employee = this.employeeOptions.find(x => x.id === this.selectedNotificationEmployeeIds[0]);
				people.push(employee ? employee.fullName : '1 person');
			} else if (this.selectedNotificationEmployeeIds.length > 1) {
				people.push(`${this.selectedNotificationEmployeeIds.length} people`);
			}

			if (people.length === 0) {
				return 'No one will be notified when this form is completed.';
			}

			return `${people.join(' and ')} will receive a link when this form is completed.`;
		},
		notificationEmployeeOptions() {
			const excludedIds = [this.selectedEmployeeId];

			if (this.notifyManager && this.selectedEmployeeManagerId) {
				excludedIds.push(this.selectedEmployeeManagerId);
			}

			return this.employeeOptions.filter(employee => employee && employee.id && !excludedIds.includes(employee.id));
		},
		canAssign() {
			return !!this.selectedFormId && !!this.selectedEmployeeId && !this.isAssigning;
		}
	},
	watch: {
		dialog: {
			immediate: true,
			handler(value) {
				if (value) {
					this.resetFields();
				}
			}
		},
		fixedFormId() {
			if (this.dialog) this.resetFields();
		},
		fixedEmployeeId() {
			if (this.dialog) this.resetFields();
		},
		selectedEmployeeId() {
			this.resetNotificationDefaults();
		},
		notifyManager(value) {
			if (value && this.selectedEmployeeManagerId) {
				this.selectedNotificationEmployeeIds = this.selectedNotificationEmployeeIds.filter(id => id !== this.selectedEmployeeManagerId);
			}
		}
	},
	methods: {
		onDialogInput(value) {
			if (!value) this.close();
		},
		close() {
			this.$emit('close');
		},
		resetFields() {
			this.selectedFormId = this.fixedFormId || null;
			this.selectedEmployeeId = this.fixedEmployeeId || null;
			this.resetNotificationDefaults();
		},
		resetNotificationDefaults() {
			this.notifyManager = this.hasEmployeeManager;
			this.selectedNotificationEmployeeIds = [];
			this.notificationDetailsExpanded = false;
		},
		submitAssign() {
			if (!this.canAssign) return;

			const excludedIds = [this.selectedEmployeeId];
			if (this.notifyManager && this.selectedEmployeeManagerId) {
				excludedIds.push(this.selectedEmployeeManagerId);
			}

			this.$emit('assign', {
				formId: this.selectedFormId,
				employeeId: this.selectedEmployeeId,
				notifyManager: this.notifyManager && this.hasEmployeeManager,
				notifyEmployeeIds: this.selectedNotificationEmployeeIds.filter(id => !excludedIds.includes(id))
			});
		}
	}
};
</script>

<style scoped>
.completion-notification {
	border-top: 1px solid rgba(0, 0, 0, 0.12);
	padding-top: 14px;
}

.completion-notification-options {
	margin-left: 34px;
	padding-top: 6px;
}
</style>
